// react components
import React, {
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react'
import axios, {
    AxiosRequestConfig,
    CancelTokenSource,
} from 'axios'
import {
    DialogContent,
    DialogTitle,
} from '@mui/material'
import {
    useDispatch,
    useSelector,
} from 'react-redux'

// components
import {
    AlertSite,
    LoaderSite,
} from 'components'

// data
import {
    contentTypeDataSite,
    contentTypeModelTypeSite,
    contentTypeSettingsDataSite,
    defaultReduxState,
    reduxFormHelpersStateSite,
    reduxFormInitialHelpersSite,
    reduxFormSetEditingSite,
    reduxFormSetIsSuccessToastOpenSite,
    reduxFormSetRefreshSite,
    reduxModalErrorEventHandlerSite,
    reduxModalMainFormHideSite,
} from 'data'

// pages
import {
    IconBlock825,
    DeleteAlert825,
    FormContent825,
    FormModalAction825,
    NotFoundComponent825,
} from 'pages'

// serializers
import {
    ContentTypeFormTypes,
    ContentTypeSettingsTypes,
    ContentTypeSettingsCustomTypes,
} from 'serializers/site'

// services
import {
    axiosErrorHandlerSite,
    checkRequiredFieldsSite,
    getApiUrlSiteV2,
    getAxiosHeadersSite,
    getDetailObjectSite,
    getFormDataV2Site,
    getFormDetailDataSite,
    getFormtMediaDataSite,
    getInputValueV2Site,
    getMainErrorMessageV2Site,
} from 'services'

// props
type FormModal825Props = {
    contentType: contentTypeModelTypeSite
    contentTypeSettingsN: ContentTypeSettingsTypes | undefined
    defaultFields?: any
    objectId: string
    onCloseHelperModal?: (e: boolean, value?: any) => void
    onRefresh?: () => void
    profileObject?: any
    setDeleted?: React.Dispatch<boolean>
    subContentTypeForced?: { id: number, name: string }
}

// main
export const FormModal825 = React.memo(React.forwardRef<{ onCloseModal: (value?: any, forceClose?: boolean) => void }, FormModal825Props>(({
    contentType,
    contentTypeSettingsN,
    defaultFields,
    objectId,
    onCloseHelperModal,
    onRefresh,
    profileObject,
    setDeleted,
    subContentTypeForced,
}, ref) => {

    const dispatch = useDispatch()
    const reduxAuth = useSelector((state: defaultReduxState) => state.reduxAuth)
    const reduxFormSite = useSelector((state: defaultReduxState) => state.reduxFormSite)
    const reduxModalSite = useSelector((state: defaultReduxState) => state.reduxModalSite)
    const reduxText = useSelector((state: defaultReduxState) => state.reduxText.data)

    type actionObjectType = {
        active?: boolean
        has_active_permission?: boolean
        has_delete_permission?: boolean
        has_edit_permission?: boolean
        has_website?: boolean
        name?: string
        original_image?: string
        staff_permission?: boolean
    }

    const [contentTypeSettings, setContentTypeSettings] = useState(contentTypeSettingsN)
    const [actionObjectState, setActionObjectState] = useState<actionObjectType>()

    const formStateProps = contentTypeSettings ? {
        contentTypeSettings: contentTypeSettings,
        data: {
            errors: {},
            fields: {},
        },
        fieldsForm: contentTypeSettings?.form.fieldsForm,
        fieldsRequired: contentTypeSettings?.form.fieldsRequired,
        structure: contentTypeSettings?.form.structure,
    } : {
        contentTypeSettings: undefined,
        data: {
            errors: {},
            fields: {},
        },
        fieldsForm: {
            formAttribute: [],
            formFile: [],
            formForeignKey: [],
            formJson: [],
            formManyToMany: [],
        },
        fieldsRequired: [],
        structure: {
            panels: [],
        },
    }

    const [axiosCancelToken, setAxiosCancelToken] = useState<CancelTokenSource | undefined>(undefined)
    const [formState, setFormState] = useState<ContentTypeFormTypes>(formStateProps)
    const [getMetaIsLoading, setGetMetaIsLoading] = useState(false)
    const [helpers, setHelpers] = useState<reduxFormHelpersStateSite>({
        ...reduxFormInitialHelpersSite,
        buttonDisabled: contentType === 'activity_registrationgroup' ? false : true,  // TODO dynamic
    })
    const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const [isUnsavedChangesAlertOpen, setIsUnsavedChangesAlertOpen] = useState(false)
    const [refreshFormNumber, setRefreshFormNumber] = useState<number | undefined>(reduxFormSite?.refresh.refreshFormSite)

    // Use ref to track if form was just submitted successfully
    const isFormJustSubmitted = useRef(false)

    // Expose onCloseModal function through ref
    useImperativeHandle(ref, () => ({
        onCloseModal,
    }), [helpers.hasChanged, onCloseHelperModal])

    const app = contentType.split('_')[0]
    const model = contentType.split('_')[1]
    const object = formState.data.fields
    const contentTypeSettingsCustom: ContentTypeSettingsCustomTypes = object?.profile?.settings_data_json?.contentTypeSettingsCustom?.[contentType] || profileObject?.settings_data_json?.contentTypeSettingsCustom?.[contentType]

    const formActive = object?.active || ([
        'accounting_customer',
        'accounting_invoice',
        'accounting_package',
        'accounting_product',
        'accounting_productprice',
        'accounting_quotation',
        'accounting_quotationline',
        'accounting_vendor',
        'activity_activity',
        'activity_course',
        'activity_coursedate',
        'activity_parent',
        'activity_period',
        'activity_registrationstatus',
        'activity_student',
        'adhesion_adherent',
        'adhesion_adherentcontent',
        'adhesion_adhesionpricing',
        'adhesion_contribution',
        'adhesion_period',
        'builder_blocktemplate',
        'builder_pagetemplate',
        'datapool_asset',
        'datapool_attribution',
        'datapool_attributioncategory',
        'datapool_status',
        'datapool_term',
        'datapool_termcategory',
        'datapool_weblink',
        'dataset_entity',
        'event_event',
        'event_eventpricing',
        'food_origin',
        'food_recipe',
        'main_city',
        'main_component',
        'main_country',
        'main_currency',
        'main_customcategory',
        'main_customchoice',
        'main_language',
        'main_team',
        'main_text',
        'miscellanea_customtable',
        'miscellanea_mailcategory',
        'portfolio_page',
        'portfolio_portfolio',
        'portfolio_portfoliocontentsource',
        'portfolio_portfoliohubcontent',
        'portfolio_portfoliopagecontent',
        'portfolio_urlredirect',
        'portfolio_userportfolio',
        'project_cssproperty',
        'project_csspropertyoption',
        'project_layout',
        'project_layoutfield',
        'project_layoutvariation',
        'project_mixedmediacontent',
        'project_project',
        'project_projectcontent',
        'project_projectpurchaselink',
        'sales_invoicingstatus',
        'sales_packageoption',
        'sales_packageoptionitem',
        'sales_product',
        'sales_productvariant',
        'sales_salesstatus',
        'sales_shippingagent',
        'sales_shippingservice',
        'sales_shippingzone',
        'sales_stripeproduct',
        'sales_stripetaxratecategory',
        'setup_module',
        'staff_mailingaccount',
        'userprofile_profile',
        'userprofile_sociallink',
        'userprofile_theme',
    ].includes(contentType) ? false : undefined)

    useEffect(() => {
        if (!contentTypeSettings) {
            getContentTypeSettingsData()
        } else {
            setContentTypeSettings(contentTypeSettings)
        }
    }, [
        contentType,
        contentTypeSettings,
    ])

    useEffect(() => {
        if (reduxFormSite.refresh.refreshFormSite !== refreshFormNumber) {
            setRefreshFormNumber(reduxFormSite.refresh.refreshFormSite)
        }
        if (contentTypeSettings) {
            getFormDetailDataSite({
                axiosCancelToken,
                component: 'FormModal825',
                contentType,
                dispatch,
                formState,
                helpers,
                noEmpty: reduxFormSite.refresh.refreshFormSite !== refreshFormNumber,
                objectId,
                reduxAuth,
                reduxModalSite,
                setAxiosCancelToken,
                setFormState,
                setHelpers,
                setInitialState,
            })
            getActionData(objectId)
        }
    }, [
        contentTypeSettings,
        objectId,
        reduxFormSite.refresh.refreshFormSite,
    ])

    function getActionData(objectId: string) {
        try {
            const refreshAxiosHeaders = getAxiosHeadersSite(reduxAuth, reduxModalSite, dispatch)

            if (!objectId || (objectId === 'new')) return
            const getUrl = getApiUrlSiteV2(`${app}/action/${model}/${objectId}/`, reduxModalSite) // Keep the last '/' for Safari !!!!!!!
            axios({
                headers: refreshAxiosHeaders,
                method: 'get',
                url: getUrl,
            })
                .then((response) => {
                    if (process.env.NODE_ENV === 'development') console.log(response)
                    const actionObject: actionObjectType = response.data
                    setActionObjectState(actionObject)
                })
                .catch((error) => {
                    axiosErrorHandlerSite({
                        apiUrl: getUrl,
                        component: 'FormModal825',
                        dispatch,
                        error,
                        reduxAuth,
                        reduxModalSite,
                        reference: 'getActionData',
                        skipSendErrorStatusCode: [404],
                    })
                })
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'FormModal825',
                'getActionData',
            ))
        }
    }

    function getContentTypeSettingsData() {
        try {
            getDetailObjectSite({
                // @ts-ignore
                apiUrl: getApiUrlSiteV2(`setup/detail/contenttypesettings/${contentTypeSettingsDataSite[contentType]}`, reduxAuth),
                component: 'FormModal825',
                dispatch,
                reduxAuth,
                reduxModalSite,
                setDetailObject: (e: any) => {
                    setContentTypeSettings(e.settings_data_json)
                    setInitialState(e.settings_data_json)
                },
            })
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'FormModal825',
                'onGetDetailData',
            ))
        }
    }

    function handleDelete() {
        try {
            const refreshAxiosHeaders = getAxiosHeadersSite(reduxAuth, reduxModalSite, dispatch)
            setIsDeleting(true)
            const deleteUrl = getApiUrlSiteV2(`${app}/form/${model}/update/${object.id}/`, reduxModalSite)
            axios({
                headers: refreshAxiosHeaders,
                method: 'delete',
                url: deleteUrl,
            })
                .then((response) => {
                    if (process.env.NODE_ENV === 'development') console.log(response)
                    setIsDeleting(false)
                    if (setDeleted) setDeleted(true)
                    isFormJustSubmitted.current = true
                    onCloseModal()
                })
                .catch((error) => {
                    setIsDeleting(false)
                    axiosErrorHandlerSite({
                        apiUrl: deleteUrl,
                        component: 'FormModal825',
                        dispatch,
                        error,
                        reduxAuth,
                        reduxModalSite,
                        reference: 'handleDelete',
                    })
                })
        } catch (error) {
            setIsDeleting(false)
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'FormModal825',
                'handleDelete',
            ))
        }
    }

    let wto2: any
    function handleInputChange(event: any) {
        try {
            if (event.name === 'link_url' && [  // TODO dynamic
                contentTypeDataSite['project_project'],
            ].includes(contentTypeSettings?.meta?.model_content_type_id!) && !object?.skip_data_retrieve) {
                setFormState(getInputValueV2Site({
                    contentType,
                    event,
                    formState,
                    reduxText,
                }))
                setHelpers({
                    ...helpers,
                    buttonDisabled: false,
                    hasChanged: true,
                    init: true,
                    mainError: '',
                })
                isFormJustSubmitted.current = false
                clearTimeout(wto2)
                wto2 = setTimeout(() => {
                    getFormtMediaDataSite({
                        contentType,
                        formState,
                        helpers,
                        image_only: false,
                        prefixImage: 'cover_',
                        reduxAuth,
                        reduxModalSite,
                        reduxText,
                        setFormState,
                        setGetMetaIsLoading,
                        setHelpers,
                        urltoParse: event.value,
                    })
                }, 1000)
            } else if (event.name === 'link_url' && [  // TODO dynamic
                contentTypeDataSite['datapool_asset'],
            ].includes(contentTypeSettings?.meta?.model_content_type_id!) && !object?.skip_data_retrieve) {
                setFormState(getInputValueV2Site({
                    contentType,
                    event,
                    formState,
                    reduxText,
                }))
                setHelpers({
                    ...helpers,
                    buttonDisabled: false,
                    hasChanged: true,
                    init: true,
                    mainError: '',
                })
                isFormJustSubmitted.current = false
                clearTimeout(wto2)
                wto2 = setTimeout(() => {
                    getFormtMediaDataSite({
                        contentType,
                        formState,
                        helpers,
                        image_only: false,
                        prefixImage: '',
                        reduxAuth,
                        reduxModalSite,
                        reduxText,
                        setFormState,
                        setGetMetaIsLoading,
                        setHelpers,
                        urltoParse: event.value,
                    })
                }, 1000)
            } else {
                setFormState(getInputValueV2Site({
                    contentType,
                    event,
                    formState,
                    reduxText,
                }))
                setHelpers({
                    ...helpers,
                    buttonDisabled: false,
                    hasChanged: true,
                    init: true,
                    mainError: '',
                })
                isFormJustSubmitted.current = false
                if (process.env.NODE_ENV === 'development') {
                    console.log('handleInputChange - hasChanged set to true')
                }
            }
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'FormModal825',
                'handleInputChange',
            ))
        }
    }

    function handleKeyPress(event: any) {
        try {
            if (event.key === 'Enter') {
                handleRequired(false, !formActive)
            }
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'FormModal825',
                'handleKeyPress',
            ))
        }
    }

    function handleRequired(stay: boolean, publish?: boolean, another?: boolean) {
        try {
            const result = checkRequiredFieldsSite({
                formInfoObject: formState,
                publish,
                reduxText,
                subContentTypeRequiredFields: contentTypeSettingsCustom?.form?.fieldsOptionsSubContentType?.[object?.subContentTypeForced?.id! || object.sub_content_type?.id],
            })
            if (result === 'ok') {
                handleSubmit(stay, publish, another)
            } else {
                setHelpers({
                    ...helpers,
                    mainError: reduxText[10222],
                })
                setFormState({
                    ...formState,
                    data: {
                        ...formState.data,
                        errors: result,
                    }
                })
            }
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'FormModal825',
                'handleRequired',
            ))
        }
    }

    function handleSubmit(stay: boolean, publish?: boolean, another?: boolean) {
        try {
            const refreshAxiosHeaders = getAxiosHeadersSite(reduxAuth, reduxModalSite, dispatch)

            setHelpers({
                ...helpers,
                buttonDisabled: true,
                isSaving: true,
                isSuccess: false,
            })

            let axiosMethod: AxiosRequestConfig['method'] = 'post'
            let axiosUrl = getApiUrlSiteV2(`${app}/form/${model}/create/`, reduxModalSite)
            if (object.id) {
                axiosMethod = 'put'
                axiosUrl = getApiUrlSiteV2(`${app}/form/${model}/update/${object.id}/`, reduxModalSite)
            }
            const formData: any = getFormDataV2Site(formState, contentTypeSettingsCustom)
            if (publish) {
                formData.append('active', 'true')
                if (contentType === 'event_event' && object.profile?.id === 193057) {  // TODO dynamic
                    formData.append('active_status', '1')
                }
            } else {
                formData.append('active', object.active ? 'true' : 'false')
            }
            axios({
                data: formData,
                headers: refreshAxiosHeaders,
                method: axiosMethod,
                url: axiosUrl,
            })
                .then((response) => {
                    if (process.env.NODE_ENV === 'development') console.log(response)
                    dispatch(reduxFormSetIsSuccessToastOpenSite(true))
                    dispatch(reduxFormSetEditingSite(false))
                    setHelpers({
                        ...helpers,
                        buttonDisabled: false,
                        hasChanged: false,
                        init: false,
                        isSaving: false,
                        isSuccess: true,
                        mainError: '',
                    })
                    // Set flag to indicate form was just submitted successfully
                    isFormJustSubmitted.current = true
                    const { id, slug, cover_image } = response.data
                    if (another) {
                        setInitialState(contentTypeSettings, object.profile)
                        if (onRefresh) onRefresh()
                        dispatch(reduxFormSetRefreshSite('refreshFormSite', id))
                    } else {
                        const baseFields = { ...object, id, slug }
                        setFormState({
                            ...formState,
                            data: {
                                ...formState.data,
                                errors: {},
                                fields: object.meta_image_url
                                    ? { ...baseFields, cover_image_original: cover_image }
                                    : baseFields
                            }
                        })
                        if (onRefresh) onRefresh()
                        onCloseModal({ id, name: response.data.name })
                        dispatch(reduxFormSetRefreshSite('refreshFormSite', id))
                    }
                })
                .catch((error) => {
                    let mainErrorMessage = ''
                    let mainErrorTitle = ''
                    if (error.response?.data?.non_field_errors) {
                        mainErrorMessage += error.response.data.non_field_errors
                    }
                    const formStateErrors = getMainErrorMessageV2Site({
                        error,
                        reduxText,
                    })
                    if (error.response?.status === 400) {
                        Object.keys(formStateErrors).forEach((key) => {
                            const name = contentTypeSettingsCustom?.field?.[key]?.verboseName || contentTypeSettings?.field?.[key]?.verboseName!
                            const label = name || key
                            mainErrorMessage += `${label}: ${formStateErrors[key]}\n`
                        })
                    } else {
                        mainErrorMessage += formStateErrors.main_error
                        mainErrorTitle += formStateErrors.main_error_title
                    }
                    const emailAndPhoneFields = Object.keys(contentTypeSettings?.field || {}).filter(key => {
                        const fieldType = contentTypeSettings?.field[key].fieldType
                        return fieldType === 'EmailField' || fieldType === 'PhoneNumberField'
                    })
                    setHelpers({
                        ...helpers,
                        buttonDisabled: false,
                        isSaving: false,
                        isSuccess: false,
                        mainError: mainErrorMessage,
                        mainErrorTitle,
                    })
                    setFormState({
                        ...formState,
                        data: {
                            ...formState.data,
                            errors: formStateErrors,
                        }
                    })
                    axiosErrorHandlerSite({
                        apiUrl: axiosUrl,
                        component: 'FormModal825',
                        dispatch,
                        error,
                        formFields: JSON.stringify(object),
                        formFieldsToIgnore: emailAndPhoneFields,
                        reduxAuth,
                        reduxModalSite,
                        reference: 'handleSubmit',
                        skipAlertStatusCode: [400, 500],
                        skipNonFieldErrors: true,
                    })
                })
        } catch (error) {
            setHelpers({
                ...helpers,
                buttonDisabled: false,
                isSaving: false,
                isSuccess: false,
            })
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'FormModal825',
                'handleSubmit',
            ))
        }
    }

    function onCloseModal(value?: any, forceClose?: boolean) {
        try {
            if (process.env.NODE_ENV === 'development') {
                console.log('onCloseModal called', {
                    hasChanged: helpers.hasChanged,
                    isFormJustSubmitted: isFormJustSubmitted.current,
                    forceClose
                })
            }
            if (helpers.hasChanged && !isFormJustSubmitted.current && !forceClose) {
                setIsUnsavedChangesAlertOpen(true)
                return
            }
            isFormJustSubmitted.current = false
            if (onCloseHelperModal) {
                onCloseHelperModal(false, value)
            } else {
                dispatch(reduxModalMainFormHideSite())
            }
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'FormModal825',
                'onCloseModal',
            ))
        }
    }

    function handleCloseWithoutSaving() {
        try {
            setIsUnsavedChangesAlertOpen(false)
            if (onCloseHelperModal) {
                onCloseHelperModal(false)
            } else {
                dispatch(reduxModalMainFormHideSite())
            }
        } catch (error) {
            dispatch(reduxModalErrorEventHandlerSite(
                error,
                'FormModal825',
                'handleCloseWithoutSaving',
            ))
        }
    }

    function setInitialState(newContentTypeSettings?: ContentTypeSettingsTypes, newProfileObject?: any) {
        const newDefaultFields = defaultFields || {}
        newDefaultFields.profile = newProfileObject || profileObject
        if (newContentTypeSettings) {
            setFormState({
                ...formStateProps,
                contentTypeSettings: contentTypeSettings,
                data: {
                    ...formStateProps.data,
                    fields: newDefaultFields,
                },
            })
        } else {
            setFormState({
                ...formStateProps,
                data: {
                    ...formStateProps.data,
                    fields: newDefaultFields,
                },
            })
        }
    }

    return (
        <React.Fragment>
            <DialogTitle className='mo-dialog-header'>
                <div className='mo-dialog-header-close-wrap reverse'>
                    <IconBlock825
                        className='mo-dialog-header-close'
                        edit={false}
                        iconClass='mo-new-icon-times-solid'
                        onClick={onCloseModal}
                    />
                </div>
                <span className='mo-dialog-header-title'>{!object?.id ? reduxText[10223] : (object?.name || reduxText[10198])}</span>
                <div className='mo-dialog-header-info-wrap reverse'>
                    <span className='mo-dialog-header-info'>{object?.id || ''}</span>
                </div>
            </DialogTitle>
            {helpers.errorStatus
                ? (
                    <DialogContent className={`mo-dialog-content mo-hidden-vertical-scrollbar${reduxModalSite.isWindows}`}>
                        <NotFoundComponent825
                            statusCode={helpers.errorStatus}
                        />
                    </DialogContent>
                ) : (
                    <>
                        <DialogContent className={`mo-dialog-content mo-hidden-vertical-scrollbar${reduxModalSite.isWindows}`}>
                            {(helpers.isLoading || !contentTypeSettings)
                                ? (
                                    <LoaderSite isOpen />
                                ) : (
                                    <FormContent825
                                        contentType={contentType}
                                        contentTypeSettings={contentTypeSettings}
                                        contentTypeSettingsCustom={contentTypeSettingsCustom}
                                        formState={formState}
                                        getMetaIsLoading={getMetaIsLoading}
                                        handleInputChange={handleInputChange}
                                        object={object}
                                        onKeyPress={handleKeyPress}
                                        subContentTypeForced={subContentTypeForced}
                                    />
                                )}
                        </DialogContent>
                        <FormModalAction825
                            actionObjectState={actionObjectState}
                            defaultFields={defaultFields}
                            formActive={formActive}
                            handleRequired={handleRequired}
                            helpers={helpers}
                            object={object}
                            onCloseModal={onCloseModal}
                            reverseButtons
                            setIsDeleteAlertOpen={setIsDeleteAlertOpen}
                        />
                    </>
                )
            }
            <AlertSite
                buttons={[{ text: reduxText[8409] }]}
                header={helpers.mainErrorTitle || reduxText[10224]}
                isOpen={Boolean(helpers.mainError)}
                message={`${helpers.mainError}${helpers.mainError === 'Network Error' ? reduxText[10225] : ''}`}
                onDidDismiss={() => setHelpers({
                    ...helpers,
                    mainError: '',
                })}
            />
            {(helpers.isSaving || isDeleting) && (
                <LoaderSite
                    isOpen
                    message={helpers.isSaving ? reduxText[10226] : (isDeleting ? reduxText[10203] : '')}
                />
            )}
            {isDeleteAlertOpen && (
                <DeleteAlert825
                    handleDelete={handleDelete}
                    objectName={object.fields?.name}
                    setIsDeleteAlertOpen={setIsDeleteAlertOpen}
                />
            )}
            {isUnsavedChangesAlertOpen && (
                <AlertSite
                    buttons={[
                        {
                            color: 'primary',
                            handler: handleCloseWithoutSaving,
                            text: reduxText[11194],
                        },
                        {
                            handler: () => setIsUnsavedChangesAlertOpen(false),
                            text: reduxText[11195],
                        },
                    ]}
                    header={reduxText[11192]}
                    isOpen
                    message={reduxText[11193]}
                    onDidDismiss={() => setIsUnsavedChangesAlertOpen(false)}
                />
            )}
        </React.Fragment>
    )

}))
